import type { RequestConfig } from "./types";
import { isApiError } from "./types";

export function serializeParams(params: Record<string, any>) {
  const serialized = {} as Record<string, string>;

  for (const key in params) {
    if (params.hasOwnProperty(key) && params[key] !== undefined) {
      serialized[key] =
        typeof params[key] === "object" ? JSON.stringify(params[key]) : params[key].toString();
    }
  }

  return serialized;
}

export function buildUrl(baseURL: string, config: RequestConfig): string {
  const path = config.url || "";

  if (path.startsWith("http")) {
    return appendParams(path, config.params);
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const fullUrl = `${baseURL.replace(/\/+$/, "")}${normalizedPath}`;
  return appendParams(fullUrl, config.params);
}

function appendParams(url: string, params?: Record<string, any>): string {
  if (!params) return url;
  const serialized = serializeParams(params);
  const qs = new URLSearchParams(serialized).toString();
  return qs ? `${url}?${qs}` : url;
}

export function buildFetchOptions(
  config: RequestConfig,
  defaultHeaders: Record<string, string>
): RequestInit {
  const headers: Record<string, string> = { ...defaultHeaders };

  if (config.headers) {
    Object.assign(headers, config.headers);
  }

  let body: BodyInit | undefined = undefined;

  if (config.data !== undefined) {
    if (config.data instanceof FormData) {
      body = config.data;
      // Let the browser set Content-Type with the correct boundary for FormData
      delete headers["Content-Type"];
    } else if (typeof config.data === "object" && config.data !== null) {
      body = JSON.stringify(config.data);
      if (!headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }
    } else {
      body = String(config.data);
    }
  }

  return {
    method: config.method || "GET",
    headers,
    body,
    credentials: config.credentials || "include",
    signal: config.signal,
  };
}

export async function parseResponse<T>(response: Response, config: RequestConfig): Promise<T> {
  if (config.responseType === "blob") {
    return (await response.blob()) as T;
  }

  if (config.responseType === "stream") {
    return response.body as T;
  }

  // Default: try JSON, fall back to text
  const text = await response.text();
  if (!text) return undefined as T;

  try {
    return JSON.parse(text);
  } catch {
    return text as T;
  }
}

export async function parseErrorResponse(response: Response): Promise<any> {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  try {
    const text = await response.text();
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Existing helpers moved from index.ts

export async function* handleStream<T>(stream: ReadableStream<Uint8Array>): AsyncGenerator<T> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");

  let done = false;

  while (!done) {
    const { value, done: readerDone } = await reader.read();
    done = readerDone;

    if (value) {
      const jsonString = decoder
        .decode(value)
        .split("data: ")
        .map((v) => v.trim())
        .filter((v) => {
          if (v === "[DONE]") return false;
          return v.length > 0;
        });

      try {
        for (const json of jsonString) {
          const jsonObject = JSON.parse(json);

          yield jsonObject as T;
        }
      } catch (error) {
        console.warn("Failed to parse JSON:", error);
      }
    }
  }

  // Close the reader
  reader.releaseLock();
}

export async function throwBlobErrorAsJson(err: unknown): Promise<never> {
  if (isApiError(err)) {
    const data = err.response?.data;
    if (data instanceof Blob) {
      const json = await blobToJSON(data);
      if (json.error) {
        (err as any).response!.data = json;
        console.error("Erro do servidor:", json.error);
        throw err;
      }
    }
  }

  throw err;
}

export async function blobToJSON(blob: Blob): Promise<any> {
  const text = await blob.text();
  try {
    return JSON.parse(text);
  } catch (error) {
    console.error("Erro ao fazer parse do Blob como JSON:", error);
    throw new Error("Erro ao processar a resposta do servidor.");
  }
}

export async function readableStreamToJSON(stream: ReadableStream): Promise<any> {
  const reader = stream.getReader();
  const decoder = new TextDecoder("utf-8");
  let result = "";

  try {
    let done = false;
    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;

      if (value) {
        result += decoder.decode(value, { stream: true });
      }
    }

    // Try to parse as JSON
    return JSON.parse(result);
  } catch (error) {
    console.error("Erro ao converter ReadableStream para JSON:", error);
    return null;
  } finally {
    reader.releaseLock();
  }
}
